<template>
  <Dialog
    v-model:visible="internalVisible"
    modal
    :header="dialogHeader"
    :style="{ width }"
    :closable="true"
    @hide="onHide"
  >
    <div v-if="resolvedAudit" class="space-y-4 text-sm">
      <div v-if="subjectLabel" class="flex items-center justify-between">
        <h3
          class="text-base font-semibold text-surface-700 dark:text-surface-200"
          data-test-id="audit-subject"
        >
          {{ subjectLabel }}
        </h3>
      </div>
      <div class="space-y-2">
        <div class="flex items-center space-x-2">
          <label class="text-surface-600 dark:text-surface-300 w-40">Created by:</label>
          <span class="text-surface-800 dark:text-surface-100" data-test-id="audit-created-by">{{ resolvedAudit.CreatedBy || '—' }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <label class="text-surface-600 dark:text-surface-300 w-40">Created on:</label>
          <span class="text-surface-800 dark:text-surface-100" data-test-id="audit-created-on">{{ formatDateForDisplay(resolvedAudit.CreatedOn) || '—' }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <label class="text-surface-600 dark:text-surface-300 w-40">Last modified by:</label>
          <span class="text-surface-800 dark:text-surface-100" data-test-id="audit-modified-by">{{ resolvedAudit.LastModifiedBy || '—' }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <label class="text-surface-600 dark:text-surface-300 w-40">Last modified on:</label>
          <span class="text-surface-800 dark:text-surface-100" data-test-id="audit-modified-on">{{ formatDateForDisplay(resolvedAudit.LastModifiedOn) || '—' }}</span>
        </div>
      </div>
      <slot />
    </div>
    <div v-else class="text-sm italic text-surface-500 dark:text-surface-400">No audit data available.</div>
    <template #footer>
      <Button label="Close" icon="pi pi-times" @click="close" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { Dialog, Button } from 'primevue'
import type { AuditRecord } from '@/types/audit'
import { formatDateForDisplay } from '@/util/dateUtils.ts'

interface Props {
  modelValue: boolean
  /** Optional explicit audit object */
  audit?: AuditRecord | null
  /** Generic subject entity; audit may be nested under .Audit if not supplied */
  subject?: any
  /** Header title */
  title?: string
  /** Optional derived label, if not provided attempts Name || MerchantName || CountryCode */
  label?: string
  /** Dialog width */
  width?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  audit: null,
  subject: null,
  title: 'Audit Information',
  label: '',
  width: '32rem'
})

const emit = defineEmits(['update:modelValue','close'])

const internalVisible = ref(props.modelValue)
watch(() => props.modelValue, v => internalVisible.value = v)
watch(internalVisible, v => emit('update:modelValue', v))

const resolvedAudit = computed(() => props.audit || props.subject?.Audit || null)

const subjectLabel = computed(() => {
  if (props.label) return props.label
  const s = props.subject || {}
  return s.Name || s.MerchantName || s.CountryCode || ''
})

const dialogHeader = computed(() => props.title)

function close() {
  internalVisible.value = false
  emit('close')
}
function onHide() {
  emit('close')
}
</script>

